<script setup lang="ts">
/**
 * Liste de factures — composant PARTAGÉ entre l'espace company-wide
 * (`PageMesFactures`) et la carte « Factures » de la page compteur.
 *
 * Régime invoice-only : AUCUN verdict, anomalie, gisement ou économie (R1).
 * Trois états par ligne (cf. `FactureVisibilite`) :
 *  - `visible`     → montants + lien « Voir la facture » ;
 *  - `a_rattacher` → montants masqués + bouton « Rattacher » ;
 *  - `a_ajouter`   → montants masqués + bouton « Ajouter ».
 *
 * Émet `voir`/`cta` vers le parent — ce composant ne décide rien (pas d'appel
 * réseau, pas de navigation).
 */
import type { FactureItem } from "@modules/courtage/types/facture-espace"

defineOptions({ name: "FacturesList" })

withDefaults(
    defineProps<{
        factures: FactureItem[]
        loading?: boolean
        /** Masque la colonne Compteur (page source : un seul compteur). */
        showCompteur?: boolean
        /** Adapte l'état vide quand des filtres/recherche sont actifs (0 résultat). */
        filtered?: boolean
    }>(),
    { loading: false, showCompteur: true, filtered: false }
)

const emit = defineEmits<{
    (e: "voir", facture: FactureItem): void
    (e: "cta", facture: FactureItem): void
}>()

const eurFormatter = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
})

function formatMontant(m: number | null): string {
    return m === null ? "—" : eurFormatter.format(m)
}

function formatPeriode(periode: [string | null, string | null]): string {
    const ref = periode[1] ?? periode[0]
    if (!ref) return "—"
    const d = new Date(ref)
    if (Number.isNaN(d.getTime())) return ref
    return d.toLocaleDateString("fr-FR", { month: "short", year: "numeric" })
}

function sourceLabel(s: FactureItem["source"]): string {
    return s === "chorus_pro" ? "Chorus Pro" : "Dépôt manuel"
}

/** Titre de ligne : compteur si connu, sinon adresse (cas `a_ajouter`). */
function titreLigne(f: FactureItem): string {
    return f.compteur_label ?? f.adresse_facture ?? "Compteur non rattaché"
}
</script>

<template>
    <div>
        <div
            v-if="loading"
            class="rounded-md bg-neutral-50 px-5 py-3.5 text-sm text-neutral-600"
        >
            Chargement…
        </div>

        <div
            v-else-if="factures.length === 0 && filtered"
            class="rounded-lg border-2 border-dashed border-neutral-200 bg-white p-12 text-center"
        >
            <p class="m-0 text-base font-medium text-neutral-700">
                Aucune facture ne correspond à votre recherche.
            </p>
            <p class="m-0 mt-2 text-sm text-neutral-500">
                Modifiez ou réinitialisez les filtres pour élargir les
                résultats.
            </p>
        </div>

        <div
            v-else-if="factures.length === 0"
            class="rounded-lg border-2 border-dashed border-neutral-200 bg-white p-12 text-center"
        >
            <p class="m-0 text-base font-medium text-neutral-700">
                Aucune facture pour l'instant.
            </p>
            <p class="m-0 mt-2 text-sm text-neutral-500">
                Déposez une facture ou connectez Chorus Pro pour démarrer.
            </p>
        </div>

        <div
            v-else
            class="overflow-hidden rounded-lg border border-neutral-200 bg-white"
        >
            <table class="w-full text-sm">
                <thead class="border-b border-neutral-200 bg-neutral-50">
                    <tr>
                        <th
                            v-if="showCompteur"
                            class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-600"
                        >
                            Compteur
                        </th>
                        <th
                            class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-600"
                        >
                            Période
                        </th>
                        <th
                            class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-600"
                        >
                            Fournisseur
                        </th>
                        <th
                            class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-neutral-600"
                        >
                            Montant TTC
                        </th>
                        <th
                            class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-600"
                        >
                            État
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="f in factures"
                        :key="f.id"
                        class="border-b border-neutral-100 last:border-b-0"
                        :class="{
                            'bg-primary-50/40': f.visibilite !== 'visible',
                        }"
                    >
                        <td v-if="showCompteur" class="px-4 py-4">
                            <div
                                class="flex items-center gap-1.5 font-medium text-neutral-800"
                            >
                                <i
                                    v-if="f.visibilite !== 'visible'"
                                    class="pi pi-lock text-xs text-primary-700"
                                    aria-hidden="true"
                                />
                                {{ titreLigne(f) }}
                            </div>
                            <div class="font-mono text-xs text-neutral-400">
                                {{ f.pdl }} ·
                                <span class="text-neutral-500">{{
                                    sourceLabel(f.source)
                                }}</span>
                            </div>
                        </td>
                        <td class="px-4 py-4 text-neutral-600">
                            {{ formatPeriode(f.periode) }}
                        </td>
                        <td class="px-4 py-4 text-neutral-800">
                            {{ f.fournisseur ?? "—" }}
                        </td>
                        <td
                            class="px-4 py-4 text-right font-medium tabular-nums"
                            :class="
                                f.montant_ttc === null
                                    ? 'text-neutral-300'
                                    : 'text-neutral-800'
                            "
                        >
                            {{ formatMontant(f.montant_ttc) }}
                        </td>
                        <td class="px-4 py-4">
                            <a
                                v-if="
                                    f.visibilite === 'visible' &&
                                    f.pdf_disponible
                                "
                                href="#"
                                class="inline-flex items-center gap-1.5 text-xs font-medium text-cyan-600 hover:text-cyan-700"
                                @click.prevent="emit('voir', f)"
                            >
                                <i class="pi pi-file" aria-hidden="true" />
                                Voir la facture
                            </a>
                            <span
                                v-else-if="f.visibilite === 'visible'"
                                class="text-xs text-neutral-400"
                            >
                                PDF indisponible
                            </span>
                            <!-- Une seule décision client : « Ajouter à mon
                                 compte » (rattacher-vs-créer = plomberie back). -->
                            <button
                                v-else
                                type="button"
                                class="inline-flex items-center gap-1.5 rounded-md border border-primary-800 bg-primary-800 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
                                @click="emit('cta', f)"
                            >
                                <i class="pi pi-plus" aria-hidden="true" />
                                Ajouter à mon compte
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
